import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { ModuleCompletionAnimation } from 'components/QuestionFlow/ModuleCompletionAnimation';
import './next-steps.css';

function AvailableCoursesIllustration() {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setRevealed(true);
        observer.disconnect();
      }
    }, { threshold: 0.25 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="course-next-steps-illustration" data-testid="available-courses-illustration" data-revealed={revealed} aria-hidden="true">
      <ModuleCompletionAnimation variant="constellation" />
    </div>
  );
}

export function NextSteps({ unlockedCourses = [] }: { unlockedCourses?: { id: number; name: string }[] }) {
  const navigate = useNavigate();

  return (
    <section aria-label="Next steps" className="px-6 pb-16">
      <div className="max-w-3xl mx-auto text-center">
        {unlockedCourses.length > 0 ? <AvailableCoursesIllustration /> : null}
        <h2 className="text-2xl md:text-3xl text-foreground mb-3">
          What&apos;s Next?
        </h2>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto text-pretty">
          {unlockedCourses.length > 0
            ? `Finishing this course unlocked ${unlockedCourses.length === 1 ? 'one of your assigned courses' : `${unlockedCourses.length} of your assigned courses`}.`
            : 'Nice work. Explore your courses to revisit what you have learned or see what is available next.'}
        </p>

        {unlockedCourses.length > 0 ? (
          <div className="mb-8 grid gap-3 text-left sm:grid-cols-2">
            {unlockedCourses.map((course) => <div key={course.id} className="flex min-w-0 flex-col items-stretch justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1"><p className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">Now available</p><h3 className="break-words font-semibold">{course.name}</h3></div>
              <Button size="auto" className="w-full sm:w-auto sm:shrink-0" onClick={() => navigate(`/training?courseId=${course.id}#courses`)} aria-label={`View ${course.name}`}>View course <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>)}
          </div>
        ) : null}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {/* Tertiary CTA */}
          <Button
            size="auto"
            variant="ghost"
            className="w-full sm:w-auto inline-flex gap-2 px-7 py-3.5 text-muted-foreground hover:text-foreground"
            type="button"
            onClick={() => navigate('/home')}
          >
            <ArrowLeft aria-hidden="true" />
            Back to Dashboard
          </Button>
          {/* Primary CTA */}
          <Button
            size="auto"
            className="group relative w-full sm:w-auto inline-flex gap-2 px-7 py-3.5"
            type="button"
            onClick={() => navigate('/training#courses')}
          >
            View Courses
            <ArrowRight
              className="transition-transform duration-200 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>
    </section>
  );
}
